import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { BehaviorSubject, Observable, combineLatest, isObservable, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

export type MultiAutocompleteOptionSource<T> = readonly T[] | Observable<readonly T[]> | null;

@Component({
  standalone: true,
  selector: 'app-generic-multi-autocomplete',
  imports: [
    CommonModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './generic-multi-autocomplete.component.html',
  styleUrl: './generic-multi-autocomplete.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => GenericMultiAutocompleteComponent),
    },
  ],
})
export class GenericMultiAutocompleteComponent<T = unknown> implements ControlValueAccessor {
  readonly fieldLabel = input('');
  readonly placeholder = input('');
  readonly options = input<MultiAutocompleteOptionSource<T>>([]);
  readonly selectedOptions = model<T[]>([]);
  readonly displayFunction = input<((option: T) => string) | null>(null);
  readonly displayProperty = input('');
  readonly identityProperty = input('');
  readonly readOnly = input(false);
  readonly disabled = input(false);
  readonly required = input(false);
  readonly emptyMessage = input('Nenhuma opção encontrada');
  readonly inputId = input('');

  readonly optionSelected = output<T>();
  readonly optionRemoved = output<T>();
  readonly selectionChanged = output<T[]>();
  readonly searchChanged = output<string>();

  readonly searchControl = new FormControl('', { nonNullable: true });
  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('autocompleteInput');
  readonly autocompleteTrigger = viewChild(MatAutocompleteTrigger);

  private readonly controlDisabled = signal(false);
  private readonly destroyRef = inject(DestroyRef);
  private readonly knownOptions = new BehaviorSubject<readonly T[]>([]);
  private destroyed = false;
  protected touched = false;
  private onChange: (value: T[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  readonly isDisabled = computed(() => this.disabled() || this.controlDisabled());

  readonly filteredOptions$: Observable<readonly T[]> = combineLatest([
    this.knownOptions,
    this.searchControl.valueChanges.pipe(startWith('')),
  ]).pipe(
    map(([options, search]) => {
      const normalizedSearch = this.normalize(search);
      const selected = this.selectedOptions() ?? [];

      return options.filter((option) => {
        const isSelected = selected.some((item) => this.sameOption(item, option));
        const matchesSearch = !normalizedSearch
          || this.normalize(this.displayOption(option)).includes(normalizedSearch);
        return !isSelected && matchesSearch;
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
    });

    effect((onCleanup) => {
      const source = this.options();
      const subscription = (isObservable(source) ? source : of(source ?? []))
        .subscribe((options) => this.rememberOptions(options ?? []));

      onCleanup(() => subscription.unsubscribe());
    });

    effect(() => this.rememberOptions(this.selectedOptions() ?? []));

    effect(() => {
      if (this.isDisabled()) {
        this.searchControl.disable({ emitEvent: false });
      } else {
        this.searchControl.enable({ emitEvent: false });
      }
    });
  }

  displayOption(option: T | null | undefined): string {
    if (option == null) {
      return '';
    }

    const displayFunction = this.displayFunction();
    if (displayFunction) {
      return displayFunction(option) ?? '';
    }

    if (typeof option === 'string' || typeof option === 'number') {
      return String(option);
    }

    const record = option as Record<string, unknown>;
    const displayProperty = this.displayProperty();
    const value = displayProperty
      ? record[displayProperty]
      : record['displayWith'] ?? record['descricao'] ?? record['nome'] ?? record['nomePessoa'];

    return value == null ? String(option) : String(value);
  }

  selectOption(event: MatAutocompleteSelectedEvent): void {
    const option = event.option.value as T;
    const current = this.selectedOptions() ?? [];

    if (!current.some((item) => this.sameOption(item, option))) {
      this.updateSelection([...current, option]);
      this.optionSelected.emit(option);
    }

    this.searchControl.setValue('');
    this.focusInput();
  }

  removeOption(option: T): void {
    if (this.readOnly() || this.isDisabled()) {
      return;
    }

    const nextSelection = (this.selectedOptions() ?? []).filter(
      (selected) => !this.sameOption(selected, option),
    );
    this.updateSelection(nextSelection);
    this.optionRemoved.emit(option);
  }

  handleSearch(value: string): void {
    this.searchChanged.emit(value);
  }

  markAsTouched(): void {
    if (!this.touched) {
      this.touched = true;
      this.onTouched();
    }
  }

  writeValue(value: T[] | null | undefined): void {
    const selection = Array.isArray(value) ? value : [];
    this.selectedOptions.set(selection);
    this.rememberOptions(selection);
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.controlDisabled.set(isDisabled);
  }

  optionIdentity(option: T): unknown {
    const property = this.identityProperty();
    return property && option && typeof option === 'object'
      ? (option as Record<string, unknown>)[property]
      : option;
  }

  private updateSelection(selection: T[]): void {
    this.selectedOptions.set(selection);
    this.onChange(selection);
    this.selectionChanged.emit(selection);
    this.markAsTouched();
  }

  private focusInput(): void {
    queueMicrotask(() => {
      if (this.destroyed) {
        return;
      }

      this.inputElement()?.nativeElement.focus();
      this.autocompleteTrigger()?.openPanel();
    });
  }

  private rememberOptions(options: readonly T[]): void {
    const known = [...this.knownOptions.value];
    for (const option of options) {
      if (!known.some((item) => this.sameOption(item, option))) {
        known.push(option);
      }
    }
    this.knownOptions.next(known);
  }

  private sameOption(first: T, second: T): boolean {
    const property = this.identityProperty();
    if (property && first && second && typeof first === 'object' && typeof second === 'object') {
      return (first as Record<string, unknown>)[property]
        === (second as Record<string, unknown>)[property];
    }
    return first === second;
  }

  private normalize(value: unknown): string {
    return String(value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLocaleLowerCase('pt-BR');
  }
}
